import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  ArrowLeft, MapPin, User, Calendar, Clock, Tag, Hash, List, 
  Info, Edit, Check, X, Send, Loader2, CheckCircle2, Shield, 
  CreditCard, Sparkles, ChevronRight, ChevronUp, ChevronLeft, AlertCircle, Settings, Car, Users
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../constants/routes.constants';
import vehicleService from '../../services/vehicleService';
import bookingService from '../../services/bookingService';
import { getImageUrl } from '../../utils/imageUtils';

const BookingPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { vehicleId } = useParams();

  // Get vehicle from location state or fetch by ID
  const [vehicle, setVehicle] = useState(location.state?.vehicle || null);
  const [isLoading, setIsLoading] = useState(!vehicle);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Booking form state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('07:30');
  const [endTime, setEndTime] = useState('23:30');
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [dateValidationError, setDateValidationError] = useState('');

  // Modal states
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
  const [showRentalRules, setShowRentalRules] = useState(false);
  const [showEditDates, setShowEditDates] = useState(false);
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Helper to format date as YYYY-MM-DD without timezone issues
  const formatDateString = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate dates (default to tomorrow and day after)
  useEffect(() => {
    if (!startDate) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setStartDate(formatDateString(tomorrow));
    }
    if (!endDate) {
      const dayAfter = new Date();
      dayAfter.setDate(dayAfter.getDate() + 2);
      setEndDate(formatDateString(dayAfter));
    }
  }, []);

  // Fetch vehicle if not in state
  useEffect(() => {
    const fetchVehicle = async () => {
      if (!vehicle && vehicleId) {
        try {
          setIsLoading(true);
          const response = await vehicleService.getVehicleById(vehicleId);
          if (response.success) {
            setVehicle(response.data);
          } else {
            navigate(ROUTES.VEHICLES);
          }
        } catch (error) {
          console.error('Error fetching vehicle:', error);
          navigate(ROUTES.VEHICLES);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchVehicle();
  }, [vehicleId, vehicle, navigate]);

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const isDateSelected = (date) => {
    if (!date) return false;
    const dateStr = formatDateString(date);
    return dateStr === startDate || dateStr === endDate || 
           (startDate && endDate && dateStr >= startDate && dateStr <= endDate);
  };

  // Calculate number of days between two dates
  const calculateDaysBetween = (start, end) => {
    if (!start || !end) return 0;
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const diffTime = Math.abs(endDateObj - startDateObj);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Get minimum rental days from vehicle
  const getMinRentalDays = () => {
    return vehicle?.minRentalDays || 1;
  };

  // Validate date selection
  const validateDateSelection = (start, end) => {
    if (!start || !end) {
      setDateValidationError('');
      return true;
    }

    const days = calculateDaysBetween(start, end);
    const minDays = getMinRentalDays();

    if (days < minDays) {
      setDateValidationError(
        `La durée minimale de location est de ${minDays} jour${minDays > 1 ? 's' : ''}. Veuillez sélectionner au moins ${minDays} jour${minDays > 1 ? 's' : ''}.`
      );
      return false;
    }

    setDateValidationError('');
    return true;
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Check if a date would show warning (less than minimum days) as end date
  // Allow any date after start date, but show warning if less than minimum
  const isDateValidForEnd = (date) => {
    if (!date || !startDate) return true;
    const dateStr = formatDateString(date);
    // Allow any date after start date (no restriction on maximum)
    return dateStr > startDate;
  };

  const handleDateClick = (date) => {
    if (!date || isDateDisabled(date)) return;
    
    const dateStr = formatDateString(date);
    
    // Case 1: No start date selected
    if (!startDate) {
      setStartDate(dateStr);
      setEndDate('');
      setDateValidationError('');
      return;
    }
    
    // Case 2: Only start date selected
    if (startDate && !endDate) {
      if (dateStr > startDate) {
        // Select end date
        setEndDate(dateStr);
        validateDateSelection(startDate, dateStr);
      } else if (dateStr < startDate) {
        // New start date (before current start)
        setStartDate(dateStr);
        setEndDate('');
        setDateValidationError('');
      } else if (dateStr === startDate) {
        // Same date clicked, set to minimum days
        const minDays = getMinRentalDays();
        const endDateObj = new Date(startDate);
        endDateObj.setDate(endDateObj.getDate() + minDays);
        const endDateStr = formatDateString(endDateObj);
        setEndDate(endDateStr);
        validateDateSelection(startDate, endDateStr);
      }
      return;
    }
    
    // Case 3: Both dates selected - allow modification
    if (startDate && endDate) {
      // If clicking on the same start date, do nothing
      if (dateStr === startDate) {
        return;
      }
      
      // If clicking on the same end date, do nothing
      if (dateStr === endDate) {
        return;
      }
      
      // If clicking before start date, change start date
      if (dateStr < startDate) {
        setStartDate(dateStr);
        validateDateSelection(dateStr, endDate);
        return;
      }
      
      // If clicking after end date, change end date (extend selection)
      if (dateStr > endDate) {
        setEndDate(dateStr);
        validateDateSelection(startDate, dateStr);
        return;
      }
      
      // If clicking between start and end, modify end date to the clicked date
      // This allows user to reduce or extend the selection
      if (dateStr > startDate && dateStr < endDate) {
        setEndDate(dateStr);
        validateDateSelection(startDate, dateStr);
        return;
      }
    }
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const formatDateDisplay = (dateStr, timeStr) => {
    if (!dateStr) return 'Non sélectionné';
    const date = new Date(dateStr);
    return `${dateStr} ${timeStr}`;
  };

  // Get vehicle display name (brand - model)
  const getVehicleDisplayName = () => {
    const brand = vehicle.specs?.brand?.name || '';
    const model = vehicle.specs?.model?.name || '';
    if (brand && model) {
      return `${brand} - ${model}`;
    } else if (brand) {
      return brand;
    } else if (model) {
      return model;
    }
    return 'Véhicule';
  };

  // Calculate booking details
  const calculateBookingDetails = () => {
    if (!startDate || !endDate || !vehicle) return null;

    // Validate minimum rental days
    const days = calculateDaysBetween(startDate, endDate);
    const minDays = getMinRentalDays();
    if (days < minDays) {
      return null; // Don't calculate if minimum days not met
    }

    const start = new Date(`${startDate}T${startTime}`);
    const end = new Date(`${endDate}T${endTime}`);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const basePrice = vehicle.pricing?.basePrice || 0;
    let totalPrice = basePrice * diffDays;
    let weeklyDiscountAmount = 0;
    let monthlyDiscountAmount = 0;
    let totalDiscount = 0;

    // Calculate weekly discount (if 7+ days and discount exists)
    if (diffDays >= 7 && vehicle.pricing?.weeklyDiscount) {
      const weeklyDiscount = vehicle.pricing.weeklyDiscount;
      if (weeklyDiscount.type === 'PERCENTAGE' && weeklyDiscount.value) {
        weeklyDiscountAmount = totalPrice * (weeklyDiscount.value / 100);
      } else if (weeklyDiscount.type === 'FIXED' && weeklyDiscount.value) {
        weeklyDiscountAmount = weeklyDiscount.value;
      }
    }

    // Calculate monthly discount (if 30+ days and discount exists)
    if (diffDays >= 30 && vehicle.pricing?.monthlyDiscount) {
      const monthlyDiscount = vehicle.pricing.monthlyDiscount;
      if (monthlyDiscount.type === 'PERCENTAGE' && monthlyDiscount.value) {
        monthlyDiscountAmount = totalPrice * (monthlyDiscount.value / 100);
      } else if (monthlyDiscount.type === 'FIXED' && monthlyDiscount.value) {
        monthlyDiscountAmount = monthlyDiscount.value;
      }
    }

    // Apply the best discount (weekly or monthly, whichever is higher)
    // If both apply, use the higher one
    totalDiscount = Math.max(weeklyDiscountAmount, monthlyDiscountAmount);

    // Apply promo code discount
    if (promoCodeApplied && promoDiscount > 0) {
      totalDiscount += promoDiscount;
    }

    const finalTotal = Math.max(0, totalPrice - totalDiscount);
    
    // Gestion de la caution : nouvelle structure avec value et managedBy
    const depositData = vehicle.pricing?.deposit;
    const depositValue = depositData?.value || (typeof depositData === 'number' ? depositData : 0); // Support ancienne structure pour compatibilité
    const depositManagedBy = depositData?.managedBy || 'AGENCY'; // Par défaut AGENCY si non spécifié
    const isDepositManagedByCarvy = depositManagedBy === 'CARVY';
    
    // Si la caution est gérée par Carvy, elle est ajoutée au montant total
    const totalWithDeposit = isDepositManagedByCarvy 
      ? finalTotal + depositValue 
      : finalTotal;

    // Determine which discount is applied (the one with higher amount)
    // If both are equal and monthly applies (≥30 days), prioritize monthly
    const appliedDiscount = (monthlyDiscountAmount > weeklyDiscountAmount) || 
                            (monthlyDiscountAmount === weeklyDiscountAmount && diffDays >= 30) 
                            ? 'monthly' : 'weekly';
    
    return {
      days: diffDays,
      basePrice,
      totalPrice,
      discount: totalDiscount,
      weeklyDiscount: weeklyDiscountAmount,
      monthlyDiscount: monthlyDiscountAmount,
      weeklyDiscountEnabled: diffDays >= 7 && !!vehicle.pricing?.weeklyDiscount,
      monthlyDiscountEnabled: diffDays >= 30 && !!vehicle.pricing?.monthlyDiscount,
      weeklyDiscountType: vehicle.pricing?.weeklyDiscount?.type,
      monthlyDiscountType: vehicle.pricing?.monthlyDiscount?.type,
      weeklyDiscountValue: vehicle.pricing?.weeklyDiscount?.value,
      monthlyDiscountValue: vehicle.pricing?.monthlyDiscount?.value,
      appliedDiscount, // Which discount is actually applied
      finalTotal,
      deposit: depositValue,
      depositManagedBy,
      isDepositManagedByCarvy,
      totalWithDeposit, // Montant total incluant la caution si gérée par Carvy
      // Debug info
      hasWeeklyDiscount: !!vehicle.pricing?.weeklyDiscount,
      hasMonthlyDiscount: !!vehicle.pricing?.monthlyDiscount,
    };
  };

  const bookingDetails = calculateBookingDetails();

  const handleApplyPromoCode = () => {
    if (promoCode.trim()) {
      const discountAmount = bookingDetails ? bookingDetails.totalPrice * 0.1 : 0;
      setPromoDiscount(discountAmount);
      setPromoCodeApplied(true);
    }
  };

  const handleRemovePromoCode = () => {
    setPromoCode('');
    setPromoCodeApplied(false);
    setPromoDiscount(0);
  };

  const handleSubmitBooking = async () => {
    if (!vehicle || !user || !bookingDetails) return;

    setIsSubmitting(true);
    try {
      const bookingData = {
        vehicleId: vehicle._id,
        startDate: `${startDate}T${startTime}`,
        endDate: `${endDate}T${endTime}`,
        promoCode: promoCodeApplied ? promoCode : undefined,
      };

      const response = await bookingService.createBooking(bookingData);
      
      if (response.success) {
        navigate(`${ROUTES.USER_DASHBOARD}/bookings/${response.data._id}`);
      } else {
        alert('Erreur lors de la création de la réservation: ' + response.message);
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Erreur lors de la création de la réservation');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Note: getImageUrl est maintenant importé depuis utils/imageUtils.js

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#27489E] mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Chargement des détails...</p>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Véhicule non trouvé</h2>
          <p className="text-gray-600 mb-6">Le véhicule que vous recherchez n'existe pas ou n'est plus disponible.</p>
          <button
            onClick={() => navigate(ROUTES.VEHICLES)}
            className="bg-[#27489E] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#1e3a7d] transition shadow-lg"
          >
            Retour aux véhicules
          </button>
        </div>
      </div>
    );
  }

  const vehicleImage = vehicle.images && vehicle.images.length > 0 
    ? getImageUrl(vehicle.images[0])
    : 'https://via.placeholder.com/400x300?text=No+Image';

  const locationString = typeof vehicle.location === 'string' 
    ? vehicle.location 
    : (vehicle.location?.city || vehicle.vehicleLocation?.cityName || 'N/A');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
      {/* Elegant Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-700 hover:text-[#27489E] transition-all group"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium">Retour</span>
            </button>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="hidden sm:inline">Réservation</span>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#27489E] font-semibold">Confirmation</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Left Side (2 columns on large screens) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Vehicle Summary Card - Enhanced */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
              <div className="relative h-64 md:h-80">
                <img
                  src={vehicleImage}
                  alt={getVehicleDisplayName()}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                
                {/* Badge */}
                <div className="absolute top-4 right-4">
                  <div className="bg-gradient-to-r from-[#27489E] to-[#1e3a7d] text-white px-5 py-2.5 rounded-full font-bold shadow-lg backdrop-blur-sm">
                    MAD {vehicle.pricing?.basePrice?.toFixed(2).replace('.', ',') || '0,00'} /jour
                  </div>
                </div>

                {/* Vehicle Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 drop-shadow-lg">
                    {getVehicleDisplayName()}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 text-white/90 text-sm">
                    <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
                      <Settings className="w-4 h-4" />
                      <span>{vehicle.specs?.transmission === 'AUTOMATIC' ? 'Automatique' : 'Manuelle'}</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
                      <Car className="w-4 h-4" />
                      <span>{vehicle.specs?.brand?.name || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
                      <Users className="w-4 h-4" />
                      <span>{vehicle.specs?.seats || 'N/A'} Places</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location & Vendor Info */}
              <div className="p-6 border-t border-gray-100">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#27489E] to-[#1e3a7d] rounded-xl flex items-center justify-center shadow-lg">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Localisation</p>
                      <p className="text-gray-900 font-semibold">{locationString}, Maroc</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#F5A623] to-[#d8941f] rounded-xl flex items-center justify-center shadow-lg">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Proposé par</p>
                      <p className="text-gray-900 font-semibold">
                        {vehicle.owner?.vendorDetails?.companyName || vehicle.owner?.fullName || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Trip Dates Card - Enhanced */}
            <div className="bg-white rounded-3xl shadow-xl p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#27489E] to-[#1e3a7d] rounded-2xl flex items-center justify-center shadow-lg">
                      <Calendar className="w-7 h-7 text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white"></div>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">Votre voyage</h2>
                    <p className="text-sm text-gray-500 flex items-center gap-2">
                      <span>Modifiez les dates si nécessaire</span>
                      {startDate && endDate && (
                        <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                          {calculateDaysBetween(startDate, endDate)} jour{calculateDaysBetween(startDate, endDate) > 1 ? 's' : ''}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEditDates(!showEditDates)}
                  className="relative p-3 hover:bg-gray-50 rounded-xl transition-all group border border-gray-200 hover:border-[#27489E]/30 hover:shadow-md"
                  aria-label={showEditDates ? "Fermer l'édition" : "Modifier les dates"}
                >
                  <Edit className={`w-5 h-5 transition-colors ${showEditDates ? 'text-[#27489E]' : 'text-gray-600 group-hover:text-[#27489E]'}`} />
                  {!showEditDates && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#27489E] rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  )}
                </button>
              </div>

              {showEditDates ? (
                <div className="space-y-6 animate-in fade-in slide-in-from-top-2">
                  {/* Calendar */}
                  <div className="bg-white rounded-2xl border border-gray-200 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Sélectionner une date</h3>
                    
                    {/* Minimum Days Info or Error Message - At the top */}
                    {dateValidationError ? (
                      <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg animate-in slide-in-from-left-2">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-red-800 mb-1">Durée minimale non respectée</p>
                            <p className="text-sm text-red-700">{dateValidationError}</p>
                            {startDate && endDate && (
                              <p className="text-xs text-red-600 mt-2">
                                Durée sélectionnée : {calculateDaysBetween(startDate, endDate)} jour{calculateDaysBetween(startDate, endDate) > 1 ? 's' : ''} | 
                                Minimum requis : {getMinRentalDays()} jour{getMinRentalDays() > 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Info className="w-4 h-4 text-blue-600" />
                          <p className="text-sm text-blue-700">
                            Durée minimale de location : <span className="font-semibold">{getMinRentalDays()} jour{getMinRentalDays() > 1 ? 's' : ''}</span>
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Month Navigation */}
                    <div className="flex items-center justify-between mb-6">
                      <button
                        onClick={() => {
                          const newMonth = new Date(currentMonth);
                          newMonth.setMonth(newMonth.getMonth() - 1);
                          setCurrentMonth(newMonth);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <ChevronLeft className="w-5 h-5 text-gray-600" />
                      </button>
                      <h4 className="text-lg font-semibold text-gray-900 capitalize">
                        {formatMonthYear(currentMonth)}
                      </h4>
                      <button
                        onClick={() => {
                          const newMonth = new Date(currentMonth);
                          newMonth.setMonth(newMonth.getMonth() + 1);
                          setCurrentMonth(newMonth);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <ChevronRight className="w-5 h-5 text-gray-600" />
                      </button>
                    </div>

                    {/* Calendar Grid */}
                    <div className="mb-6">
                      {/* Days of week header */}
                      <div className="grid grid-cols-7 gap-2 mb-2">
                        {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((day, index) => (
                          <div key={index} className="text-center text-sm font-semibold text-gray-600 py-2">
                            {day}
                          </div>
                        ))}
                      </div>
                      
                      {/* Calendar days */}
                      <div className="grid grid-cols-7 gap-2">
                        {getDaysInMonth(currentMonth).map((date, index) => {
                          if (!date) {
                            return <div key={index} className="aspect-square" />;
                          }
                          
                          const dateStr = formatDateString(date);
                          const isSelected = isDateSelected(date);
                          const isDisabled = isDateDisabled(date);
                          const isStart = dateStr === startDate;
                          const isEnd = dateStr === endDate;
                          const isInRange = startDate && endDate && dateStr > startDate && dateStr < endDate;
                          // Include end date in the range for green background (all dates except start)
                          const isInRangeIncludingEnd = startDate && endDate && dateStr > startDate && dateStr <= endDate;
                          
                          // Check if this date would show warning (less than minimum days)
                          let isInvalidEndDate = false;
                          if (startDate && !endDate && dateStr > startDate) {
                            const days = calculateDaysBetween(startDate, dateStr);
                            const minDays = getMinRentalDays();
                            isInvalidEndDate = days < minDays; // Show warning but allow selection
                          }
                          
                          return (
                            <button
                              key={index}
                              onClick={() => handleDateClick(date)}
                              disabled={isDisabled}
                              className={`
                                aspect-square rounded-lg transition-all relative
                                ${isDisabled 
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                  : isStart || isInRangeIncludingEnd
                                    ? dateValidationError && (isStart || isEnd)
                                      ? 'bg-red-500 text-white font-semibold shadow-md'
                                      : 'bg-green-500 text-white font-semibold'
                                    : isInvalidEndDate
                                      ? 'bg-red-50 text-red-600 border-2 border-red-200 opacity-60'
                                      : 'bg-white text-gray-900 hover:bg-blue-50 border border-gray-200 hover:border-[#27489E]'
                                }
                                ${!isDisabled && !isInRangeIncludingEnd && !isStart && !isInvalidEndDate ? 'hover:scale-105' : ''}
                              `}
                              title={
                                isInvalidEndDate 
                                  ? `Minimum ${getMinRentalDays()} jour(s) requis` 
                                  : isInRangeIncludingEnd 
                                    ? `Sélectionné (${dateStr})` 
                                    : ''
                              }
                            >
                              <div className="flex flex-col items-center justify-center h-full">
                                <span className="text-sm font-medium">{date.getDate()}</span>
                                {!isDisabled && !isSelected && !isInvalidEndDate && (
                                  <span className="text-xs text-gray-500 mt-0.5">
                                    MAD {vehicle?.pricing?.basePrice?.toFixed(0) || '0'}
                                  </span>
                                )}
                                {isInvalidEndDate && (
                                  <span className="text-xs text-red-500 mt-0.5 font-semibold">Min</span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>



                    {/* Legend */}
                    <div className="flex items-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-gray-300"></div>
                        <span className="text-gray-600">Non disponible</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-green-500"></div>
                        <span className="text-gray-600">Sélectionné pour la réservation</span>
                      </div>
                    </div>
                  </div>

                  {/* Time Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">Heure de début</label>
                      <div className="relative">
                        <input
                          type="time"
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                          className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all text-gray-900 font-medium cursor-pointer hover:border-gray-300"
                        />
                        <Clock className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">Heure de fin</label>
                      <div className="relative">
                        <input
                          type="time"
                          value={endTime}
                          onChange={(e) => setEndTime(e.target.value)}
                          className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all text-gray-900 font-medium cursor-pointer hover:border-gray-300"
                        />
                        <Clock className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                      </div>
                    </div>
                  </div>

                  {/* Selected Dates Display */}
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-[#27489E]" />
                      <div>
                        <p className="text-xs text-gray-500">Date de début du trajet</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatDateDisplay(startDate, startTime)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-[#27489E]" />
                      <div>
                        <p className="text-xs text-gray-500">Date de fin du trajet</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatDateDisplay(endDate, endTime)}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      if (startDate && endDate && validateDateSelection(startDate, endDate)) {
                        setShowEditDates(false);
                      }
                    }}
                    disabled={!startDate || !endDate || !!dateValidationError}
                    className={`
                      w-full py-3.5 rounded-xl font-semibold transition-all transform
                      ${!startDate || !endDate || dateValidationError
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-[#27489E] to-[#1e3a7d] text-white hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]'
                      }
                    `}
                  >
                    {dateValidationError ? 'Corriger les dates' : 'Confirmer les dates'}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Start Date Card - Modern Design */}
                  <button
                    onClick={() => setShowEditDates(true)}
                    className="relative bg-white rounded-2xl p-6 border border-gray-200 hover:border-[#27489E]/30 hover:shadow-xl transition-all duration-300 text-left group cursor-pointer overflow-hidden"
                  >
                    {/* Decorative gradient background */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-transparent rounded-full -mr-16 -mt-16 opacity-50 group-hover:opacity-70 transition-opacity"></div>
                    
                    {/* Content */}
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-[#27489E] to-[#1e3a7d] rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-300">
                            <Calendar className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Début du trajet</p>
                            <p className="text-xs text-gray-400 mt-0.5">Date de départ</p>
                          </div>
                        </div>
                        <div className="w-2 h-2 bg-blue-500 rounded-full group-hover:scale-150 transition-transform"></div>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-2xl font-bold text-gray-900 leading-tight">
                          {startDate ? new Date(startDate).toLocaleDateString('fr-FR', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric' 
                          }) : 'Non sélectionné'}
                        </p>
                        <div className="flex items-center gap-2 pt-2">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-blue-50 transition-colors">
                            <Clock className="w-4 h-4 text-gray-600 group-hover:text-[#27489E] transition-colors" />
                          </div>
                          <span className="text-base font-semibold text-gray-700">{startTime}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Hover effect border */}
                    <div className="absolute inset-0 border-2 border-[#27489E] rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity pointer-events-none"></div>
                  </button>
                  
                  {/* End Date Card - Modern Design */}
                  <button
                    onClick={() => setShowEditDates(true)}
                    className="relative bg-white rounded-2xl p-6 border border-gray-200 hover:border-emerald-300 hover:shadow-xl transition-all duration-300 text-left group cursor-pointer overflow-hidden"
                  >
                    {/* Decorative gradient background */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-50 to-transparent rounded-full -mr-16 -mt-16 opacity-50 group-hover:opacity-70 transition-opacity"></div>
                    
                    {/* Content */}
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-300">
                            <Calendar className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Fin du trajet</p>
                            <p className="text-xs text-gray-400 mt-0.5">Date de retour</p>
                          </div>
                        </div>
                        <div className="w-2 h-2 bg-emerald-500 rounded-full group-hover:scale-150 transition-transform"></div>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-2xl font-bold text-gray-900 leading-tight">
                          {endDate ? new Date(endDate).toLocaleDateString('fr-FR', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric' 
                          }) : 'Non sélectionné'}
                        </p>
                        <div className="flex items-center gap-2 pt-2">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-50 transition-colors">
                            <Clock className="w-4 h-4 text-gray-600 group-hover:text-emerald-600 transition-colors" />
                          </div>
                          <span className="text-base font-semibold text-gray-700">{endTime}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Hover effect border */}
                    <div className="absolute inset-0 border-2 border-emerald-500 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity pointer-events-none"></div>
                  </button>
                </div>
              )}
            </div>

            {/* Promo Code Card - Enhanced */}
            <div className="bg-white rounded-3xl shadow-xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Tag className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Code promo</h3>
              </div>
              {!promoCodeApplied ? (
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Tag className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Entrez votre code promo"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-transparent transition-all"
                    />
                  </div>
                  <button
                    onClick={handleApplyPromoCode}
                    disabled={!promoCode.trim()}
                    className="px-6 py-3.5 bg-gradient-to-r from-[#27489E] to-[#1e3a7d] text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 disabled:hover:scale-100"
                  >
                    Appliquer
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-green-800 font-bold">{promoCode}</p>
                      <p className="text-green-600 text-sm">Code appliqué avec succès</p>
                    </div>
                  </div>
                  <button
                    onClick={handleRemovePromoCode}
                    className="p-2 hover:bg-green-200 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-green-700" />
                  </button>
                </div>
              )}
            </div>

            {/* Cancellation Policy & Rules - Enhanced */}
            <div className="space-y-4">
              {/* Rental Rules */}
              {vehicle.rules && vehicle.rules.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
                  <button
                    onClick={() => setShowRentalRules(!showRentalRules)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <List className="w-5 h-5 text-gray-600" />
                      <h3 className="text-lg font-bold text-gray-900">Règles du véhicule</h3>
                    </div>
                    <ChevronUp className={`w-5 h-5 text-gray-400 transition-transform ${showRentalRules ? '' : 'rotate-180'}`} />
                  </button>
                  {showRentalRules && (
                    <div className="px-4 pb-4 pt-0 border-t border-gray-200">
                      <div className="space-y-2 mt-3">
                        {vehicle.rules.map((rule, index) => (
                          <div key={index} className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-[#27489E] flex-shrink-0 mt-0.5" />
                            <p className="text-gray-700 text-sm leading-relaxed">
                              {typeof rule === 'object' && rule !== null ? (rule.name || rule.description) : rule}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Cancellation Policy */}
              {vehicle.cancellationPolicies && vehicle.cancellationPolicies.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
                  <button
                    onClick={() => setShowCancellationPolicy(!showCancellationPolicy)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <Hash className="w-5 h-5 text-[#27489E]" />
                      <h3 className="text-lg font-bold text-gray-900">
                        {t('vehicleDetail.cancellationPolicy')}
                      </h3>
                    </div>
                    <ChevronUp className={`w-5 h-5 text-gray-400 transition-transform ${showCancellationPolicy ? '' : 'rotate-180'}`} />
                  </button>
                  {showCancellationPolicy && (
                    <div className="px-4 pb-4 pt-0 border-t border-gray-200">
                      <p className="text-base font-bold text-gray-900 mt-4 mb-3 leading-snug">
                        {t('vehicleDetail.cancellationPolicy')}
                      </p>
                      <div className="space-y-2">
                        {vehicle.cancellationPolicies.map((policy, index) => (
                          <div key={index} className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-[#27489E] flex-shrink-0 mt-0.5" />
                            <p className="text-gray-700 text-sm leading-relaxed">
                              {policy.policy?.description?.replace(/x%/gi, `${policy.percentage}%`) || 
                               policy.policy?.name || 
                               `Déduction de ${policy.percentage}% en cas d'annulation`}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Additional Info Card */}
            <div className="bg-white rounded-3xl shadow-xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Info className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Informations complémentaires</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <span className="text-gray-700 font-medium">Fumer autorisé</span>
                  <span className={`px-3 py-1.5 rounded-full font-semibold text-sm ${
                    vehicle.smokingAllowed 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {vehicle.smokingAllowed ? 'Oui' : 'Non'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <span className="text-gray-700 font-medium">Voyage international</span>
                  <span className={`px-3 py-1.5 rounded-full font-semibold text-sm ${
                    vehicle.internationalTravelAllowed 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {vehicle.internationalTravelAllowed ? 'Oui' : 'Non'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Price Summary Sidebar - Sticky */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#27489E] to-[#1e3a7d] p-6 text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <CreditCard className="w-6 h-6" />
                    <h3 className="text-xl font-bold">Résumé de la réservation</h3>
                  </div>
                  <p className="text-blue-100 text-sm">Vérifiez les détails avant de confirmer</p>
                </div>

                {/* Price Details */}
                {bookingDetails && (
                  <div className="p-6 space-y-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600">Prix ({bookingDetails.days} {bookingDetails.days > 1 ? 'jours' : 'jour'})</span>
                        <span className="text-gray-900 font-semibold">
                          MAD {bookingDetails.totalPrice.toFixed(2).replace('.', ',')}
                        </span>
                      </div>
                      
                      {/* Weekly Discount - Only show if it's the applied discount or if monthly doesn't apply */}
                      {vehicle.pricing?.weeklyDiscount && (
                        (bookingDetails.appliedDiscount === 'weekly' || !bookingDetails.monthlyDiscountEnabled) && (
                          <div className={`flex justify-between items-center py-2 rounded-xl px-3 border ${
                            bookingDetails.weeklyDiscount > 0 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-gray-50 border-gray-200 opacity-60'
                          }`}>
                            <div className="flex flex-col">
                              <span className={`font-medium text-sm ${
                                bookingDetails.weeklyDiscount > 0 ? 'text-green-700' : 'text-gray-500'
                              }`}>
                                Réduction hebdomadaire
                              </span>
                              {bookingDetails?.weeklyDiscountValue && (
                                <span className={`text-xs ${
                                  bookingDetails.weeklyDiscount > 0 ? 'text-green-600' : 'text-gray-400'
                                }`}>
                                  {bookingDetails.weeklyDiscountType === 'PERCENTAGE' 
                                    ? `${bookingDetails.weeklyDiscountValue}%` 
                                    : `MAD ${bookingDetails.weeklyDiscountValue?.toFixed(2) || '0'}`}
                                  {bookingDetails.days < 7 && (
                                    <span className="ml-1 text-gray-400">(min. 7 jours)</span>
                                  )}
                                </span>
                              )}
                            </div>
                            <span className={`font-bold ${
                              bookingDetails?.weeklyDiscount > 0 ? 'text-green-700' : 'text-gray-400'
                            }`}>
                              {bookingDetails?.weeklyDiscount > 0 
                                ? `- MAD ${bookingDetails.weeklyDiscount.toFixed(2).replace('.', ',')}` 
                                : bookingDetails?.days < 7 ? 'Min. 7 jours' : 'Non applicable'}
                            </span>
                          </div>
                        )
                      )}

                      {/* Monthly Discount - Only show if it's the applied discount */}
                      {vehicle.pricing?.monthlyDiscount && (
                        bookingDetails.monthlyDiscountEnabled && (
                          <div className={`flex justify-between items-center py-2 rounded-xl px-3 border ${
                            bookingDetails.monthlyDiscount > 0 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-gray-50 border-gray-200 opacity-60'
                          }`}>
                            <div className="flex flex-col">
                              <span className={`font-medium text-sm ${
                                bookingDetails.monthlyDiscount > 0 ? 'text-green-700' : 'text-gray-500'
                              }`}>
                                Réduction mensuelle
                              </span>
                              {bookingDetails?.monthlyDiscountValue && (
                                <span className={`text-xs ${
                                  bookingDetails.monthlyDiscount > 0 ? 'text-green-600' : 'text-gray-400'
                                }`}>
                                  {bookingDetails.monthlyDiscountType === 'PERCENTAGE' 
                                    ? `${bookingDetails.monthlyDiscountValue}%` 
                                    : `MAD ${bookingDetails.monthlyDiscountValue?.toFixed(2) || '0'}`}
                                  {bookingDetails.days < 30 && (
                                    <span className="ml-1 text-gray-400">(min. 30 jours)</span>
                                  )}
                                </span>
                              )}
                            </div>
                            <span className={`font-bold ${
                              bookingDetails?.monthlyDiscount > 0 ? 'text-green-700' : 'text-gray-400'
                            }`}>
                              {bookingDetails?.monthlyDiscount > 0 
                                ? `- MAD ${bookingDetails.monthlyDiscount.toFixed(2).replace('.', ',')}` 
                                : bookingDetails?.days < 30 ? 'Min. 30 jours' : 'Non applicable'}
                            </span>
                          </div>
                        )
                      )}

                      {/* Promo Code Discount */}
                      {promoCodeApplied && promoDiscount > 0 && (
                        <div className="flex justify-between items-center py-2 bg-blue-50 rounded-xl px-3 border border-blue-200">
                          <span className="text-blue-700 font-medium text-sm">Code promo ({promoCode})</span>
                          <span className="text-blue-700 font-bold">- MAD {promoDiscount.toFixed(2).replace('.', ',')}</span>
                        </div>
                      )}

                      {/* Total Discount (if multiple discounts) */}
                      {bookingDetails.discount > 0 && (bookingDetails.weeklyDiscount > 0 || bookingDetails.monthlyDiscount > 0 || promoDiscount > 0) && (
                        <div className="flex justify-between items-center py-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl px-3 border-2 border-green-300">
                          <span className="text-green-800 font-bold text-sm">Total des réductions</span>
                          <span className="text-green-800 font-bold">- MAD {bookingDetails.discount.toFixed(2).replace('.', ',')}</span>
                        </div>
                      )}

                      {/* Afficher la caution séparément seulement si elle est gérée par l'agence */}
                      {bookingDetails.deposit > 0 && !bookingDetails.isDepositManagedByCarvy && (
                        <div className="flex justify-between items-center py-2 border-t border-gray-200 pt-3">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-600 text-sm">Caution (gérée par l'agence)</span>
                          </div>
                          <span className="text-gray-900 font-semibold">
                            MAD {bookingDetails.deposit.toFixed(2).replace('.', ',')}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Total */}
                    <div className="border-t-2 border-gray-200 pt-4 mt-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-lg font-bold text-gray-900">Total à payer</span>
                        <span className="text-2xl font-bold text-[#27489E]">
                          MAD {bookingDetails.isDepositManagedByCarvy 
                            ? bookingDetails.totalWithDeposit.toFixed(2).replace('.', ',')
                            : bookingDetails.finalTotal.toFixed(2).replace('.', ',')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {bookingDetails.deposit > 0 && bookingDetails.isDepositManagedByCarvy && (
                          <span>Inclut MAD {bookingDetails.deposit.toFixed(2).replace('.', ',')} de caution (gérée par <span className="text-[#27489E]">Car</span><span className="text-orange-500">vy</span>)</span>
                        )}
                        {bookingDetails.deposit > 0 && !bookingDetails.isDepositManagedByCarvy && (
                          <span>+ MAD {bookingDetails.deposit.toFixed(2).replace('.', ',')} de caution à payer à l'agence</span>
                        )}
                      </p>
                    </div>

                    {/* Security Badge */}
                    <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <Shield className="w-5 h-5 text-blue-600" />
                      <p className="text-sm text-blue-800">
                        <span className="font-semibold">Paiement sécurisé</span>
                        <br />
                        <span className="text-blue-600">Vos données sont protégées</span>
                      </p>
                    </div>

                    {/* CTA Button */}
                    <button
                      onClick={handleSubmitBooking}
                      disabled={!bookingDetails || isSubmitting || !startDate || !endDate}
                      className="w-full bg-gradient-to-r from-[#27489E] to-[#1e3a7d] text-white py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-3 mt-6"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Traitement en cours...</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5" />
                          <span>Confirmer et payer</span>
                        </>
                      )}
                    </button>

                    {/* Trust Indicators */}
                    <div className="flex items-center justify-center gap-6 pt-4 border-t border-gray-200 mt-4">
                      <div className="flex flex-col items-center gap-1">
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                        <span className="text-xs text-gray-500">Garantie</span>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <Shield className="w-5 h-5 text-blue-500" />
                        <span className="text-xs text-gray-500">Sécurisé</span>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <Clock className="w-5 h-5 text-purple-500" />
                        <span className="text-xs text-gray-500">Rapide</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
